import React, { useState, useEffect, useCallback, useRef } from "react";
import "./index.scss";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  Modal,
  SideMenu,
  SideMenuAccordion,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import classNames from "classnames";
import Favorite from "./pages/Favorite";
import MyInbox from "./pages/MyInbox";
import Trash from "./pages/Trash";
import HeaderUserDropdown from "./components/HeaderUserDropdown";
import WorkgroupListPage from "./pages/WorkgroupList";
import {
  getAccessToken,
  getAdminInfoFromLocalStorage,
  getUserInfoFromLocalStorage,
  getWorkspaceCode,
  getWorkspaceName,
  removeAccessAdminToken,
  removeAccessToken,
  removeAdminInfoFromLocalStorage,
  removeUserInfoFromLocalStorage,
  setRedirectUrl,
  setUserInfoFromLocalStorage,
  setWorkspaceCode,
  setWorkspaceName,
} from "./shared/utils/auth";
import WorkgroupAddModal from "./components/WorkgroupAddModal";
import WorkgroupPage from "./pages/Workgroup";
import Notification from "./components/Notification";
import useLoginStore from "./redux/dispatcher/useLoginStore";
import { AlarmRequest, CsChat } from "./types/common";
import useWorkgroupStore from "./redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "./types/workgroup";
import { LoginDto } from "./types/auth";
import utilsCommon from "./shared/utils/common";
import Search from "./pages/Search";
import docApi from "./redux/api/DocApi";
import { nanoid } from "@reduxjs/toolkit";
import useDocumentStore from "./redux/dispatcher/useDocumentStore";
import AiChatPage from "./components/AIChatPage";
import AIChatIconMenu from "./components/AIChatPage/AIChatIconMenu";
import { DocInfo, UploadFile } from "./types/files";
import { useTranslation } from "react-i18next";
import HomePage from "./pages/Home";
import { PRODUCT_ITEM_TYPE } from "./redux/api/appApi";
import WrapIcon from "./shared/icons/WrapIcon";
import DividerIcon from "./shared/icons/DividerIcon";
import useAppStore from "./redux/dispatcher/useAppStore";
import LinkApp from "./components/LinkApp";
import LinkAppPortal from "./components/LinkApp/LinkAppPortal";
import RootSearchbar from "./components/RootSearchbar";
import WorkspaceAvatar, { pickColor } from "./components/WorkspaceAvatar";
import { WorkspaceDTO } from "./redux/store/workspace";
import useWorkspaceStore from "./redux/dispatcher/useWorkspaceStore";
import CloudIcon from "./shared/icons/CloudIcon";
import i18n from "./locales/i18n";
import workspaceUtil from "./shared/utils/workspaceUtil";
import utilsDocs from "./shared/utils/docs";
import LinkPortalPage from "./pages/LinkPortalPage";
import WorkgroupLog from "./pagesAdmin/WorkgroupLog";
import AdminSetting from "./pagesAdmin/Dept";
import Config from "./pagesAdmin/Config";
import DocLog from "./pagesAdmin/DocLog";
import config from "./shared/config/config";
import ManageStorage from "./pages/ManageStorage";
import WorkgroupManage from "./pagesAdmin/Workgroup";
import NestedSidemenu from "./components/NestedSidemenu";
import WorkgroupAvatar from "./components/WorkgroupAvatar";
import axios from "./shared/utils/axios";

export const SIDE_WORKGROUPLIST_FILTER = {
  isDetail: "Y",
  managerId: "",
  orderBy: "workgroupName",
  orderByType: "ASC",
  pageRange: 1,
  posPage: 1,
  rowRange: 0,
  searchDate: {
    start: 0,
    end: 0,
  },
  status: 1,
  workgroupName: "",
};
function BaseLayout() {
  const token = getAccessToken() as string | "";
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const GATEWAY_URL = config.servers.gateway.url;

  /** Hook 정리 */
  let panelRef = useRef<any>();
  const toasts = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginState, getAlarmList } = useLoginStore();
  const { updateWorkspaceMember, deleteMember } = useWorkspaceStore();
  const {
    workgroupState,
    getWorkgroupSideList,
    getWorkspaceConfig,
    getMembersProfile,
  } = useWorkgroupStore();
  const { appState, getAppLoginInfo, uploadDropboxToEco, uploadEcoToDrive } =
    useAppStore();
  const {
    documentState,
    changeDragItem,
    setAddUploadProgressFiles,
    setUpdateUploadProgressFiles,
    completeCheckinByWeb,
    getDocUploadUidCreate,
    doDocUploadFileComplete,
  } = useDocumentStore();
  const { workspaceStore, getWorkspaceList } = useWorkspaceStore();
  /** State 정리 */
  const [tab, changeTab] = useState(
    loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
      ? `/myInbox/received`
      : "/home"
  ); // 현재 사이드 메뉴 위치
  const [pageReady, setPageReady] = useState(false); // 페이지 로딩 안되었을 경우엔 빈 화면 표시
  const [sideMenuSortby, changeSidemeSortBy] = useState(true);
  const [openExtraModal, changeOpenExtraModal] = useState("CLOSE");
  const [openAiFloatChatPage, toggleOpenFloatAiPage] = useState(false);
  const [dragFiles, changeDragFiles] = useState<DocInfo[]>([]);
  const [targetOpenAppLinkPortal, changeTargetOpenAppLinkPortal] = useState<
    PRODUCT_ITEM_TYPE | undefined
  >();
  const [openAddLinkModal, toggleOpenAddLinkModal] = useState(false);
  const [openFileUploadModal, toggleOpenFileUploadModal] = useState(false);
  const [tenancyInfo, changeTenancyInfo] = useState<WorkspaceDTO>();
  const [buttonClick, setButtonClick] = useState(0);
  const [confirmUnregisterModalId, changeConfirmUnregisterModalId] =
    useState(""); // 스페이스 탈퇴

  //메뉴 오른쪽으로 크기 드래그
  const [mouseDown, setMouseDown] = useState(false);

  //사이드바 실시간으로 크기 조절
  const handleResize = (movementX: number) => {
    const panel = panelRef.current;
    if (!panel) return;
    else {
      const { width } = panel.getBoundingClientRect();
      let result = width + movementX;
      if (result > 400) {
        result = 400;
      }
      if (result < 240) {
        result = 240;
      }
      result = Math.floor(result);
      panel.style.width = `${result}px`;
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setMouseDown(true);
  };

  useEffect(() => {
    const handleMouseUp = () => setMouseDown(false);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const ratio = window.devicePixelRatio;
      handleResize(e.movementX / ratio);
    };
    if (mouseDown) {
      window.addEventListener("mousemove", handleMouseMove);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [mouseDown]);
  useEffect(() => {
    const reqDto: AlarmRequest = { userId: "", logId: "", order: "" };

    if (window.eco.serviceType === "OCI") {
      const params = new URL(window.location.href).searchParams;

      getWorkspaceList()
        .then((resp: any) => {
          const tempUrlDsdCode = window.location.pathname.split("/")[1];
          const tempUrlAlias = decodeURI(
            window.location.pathname.split("/")[1] ?? ""
          );
          const targetTenancy = resp.payload?.filter(
            (item: WorkspaceDTO) =>
              // (item.alias === getWorkspaceName() &&
              //   item.dsdCode !== tempUrlDsdCode) ||
              item.dsdCode === tempUrlDsdCode || item.alias === tempUrlAlias
          )[0];
          if (params.has("needExtraWork") && targetTenancy) {
            if (params.has("successMsg")) {
              toasts.toastMsg(
                nanoid(),
                params.get("successMsg") ?? "",
                "success"
              );
            } else if (params.has("errorMsg")) {
              toasts.toastMsg(nanoid(), params.get("errorMsg") ?? "", "error");
            }
            navigate(
              config.url(`/${targetTenancy?.alias}/linkPortal?type=pay`)
            );
          } else if (
            targetTenancy &&
            targetTenancy?.tenancyCode === "" &&
            targetTenancy?.userAccessStatus === "REQUIRE_2FA"
          ) {
            window.location.href = `${
              process.env.PUBLIC_URL
            }/My Space/home?require2fa=${encodeURIComponent(
              targetTenancy.alias
            )}`;
            return;
          } else if (
            targetTenancy &&
            !(
              targetTenancy?.alias === getWorkspaceName() &&
              targetTenancy.dsdCode !== tempUrlDsdCode &&
              targetTenancy.alias !== tempUrlAlias
            )
          ) {
            setWorkspaceName(targetTenancy?.alias);
            let newPlace = window.location.pathname.split("/");
            newPlace[1] = targetTenancy?.alias;
            navigate(config.url(newPlace.join("/")) + window.location.search);
          }
          if (!token) return; // 로그아웃 후 workspaceCode 제거로 인한 재실행 방지

          setWorkspaceCode(targetTenancy?.dsdCode ?? "MTECOCOMMON00001");
          getWorkgroupSideList({
            ...SIDE_WORKGROUPLIST_FILTER,
            orderByType: sideMenuSortby ? "ASC" : "DESC",
          });
          changeTenancyInfo(targetTenancy);
          getWorkspaceConfig();
          const curUser = getUserInfoFromLocalStorage() as string | "";
          let newLoginDto: LoginDto = JSON.parse(curUser);
          newLoginDto = {
            ...newLoginDto,
            ecoUserInfo: {
              ...newLoginDto.ecoUserInfo,
              admin: targetTenancy?.masterUserId === myId,
              status: targetTenancy?.userStatus,
            },
          };
          setUserInfoFromLocalStorage(newLoginDto);

          getAlarmList(reqDto);
          setInterval(async () => {
            await getAlarmList(reqDto);
          }, 5 * 60 * 1000); // 5min
          setUserInfoToState();
        })
        .catch((err) => {
          setUserInfoToState();
        });
    } else {
      getWorkgroupSideList({
        ...SIDE_WORKGROUPLIST_FILTER,
        orderByType: sideMenuSortby ? "ASC" : "DESC",
      });
      getAlarmList(reqDto);
      setInterval(async () => {
        await getAlarmList(reqDto);
      }, 5 * 60 * 1000); // 5min
      setUserInfoToState();
    }
  }, [window.location.href, getWorkspaceCode()]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    changeTab(window.location.pathname);
    toggleOpenFloatAiPage(false);
    const path = window.location.pathname.split("/");
    if (path.length >= 2) {
      const tabName = window.eco.serviceType !== "OCI" ? path[1] : path[2];
      const workgroupId = window.eco.serviceType !== "OCI" ? path[2] : path[3];
      switch (decodeURI(tabName)) {
        case "":
        case "trash":
        case "favorite":
        case "log":
        case "search":
        case "login":
        case "error":
        case "admin":
          // case "demo-zoom-callback.html":
          return;
        case "home":
        case "workgroups":
          if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE")
            window.location.href = `${process.env.PUBLIC_URL}/error`;
          return;
        case "myInbox":
          if (workgroupId && ["sent", "received"].includes(workgroupId)) return;
          window.location.href =
            utilsCommon.renderProperNavigatePath("/myInbox/received");
          return;
        case "workgroup":
          if (loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE")
            window.location.href = `${process.env.PUBLIC_URL}/error`;
          if (
            workgroupState.workgroupSidemenuList?.length > 0 &&
            workgroupState.workgroupSidemenuList?.filter(
              (workgroup: Workgroup) =>
                workgroup.workgroupId === workgroupId &&
                workgroup.workgroupJoined !== false &&
                (workgroup.isValidate === 1 || workgroup.managerId === myId)
            ).length === 0
          ) {
            toasts.toastMsg(nanoid(), t("common.alert.noneWorkgroup"), "error");
            window.location.href = utilsCommon.renderProperNavigatePath(
              loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                ? `/myInbox/received`
                : "/home"
            );
          }
          return;
        case "linkPortal":
          if (window.eco.serviceType === "OCI") {
            if (
              params.get("type") !== "manage" ||
              getWorkspaceName() === "My Space" ||
              (tenancyInfo && tenancyInfo?.masterUserId === myId)
            ) {
              return;
            } else {
              window.location.href = utilsCommon.renderProperNavigatePath(
                loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                  ? `/myInbox/received`
                  : "/home"
              );
            }
          } else {
            window.location.href = `${process.env.PUBLIC_URL}/error`;
          }
          return;
        case "manageStorage":
        case "manageWorkgroup":
        case "manageDept":
        case "manageConfig":
        case "workgroupLog":
        case "fileLog":
          if (
            window.eco.serviceType !== "OCI" ||
            (getWorkspaceName() !== "My Space" &&
              tenancyInfo &&
              tenancyInfo?.masterUserId !== myId)
          ) {
            window.location.href = `${process.env.PUBLIC_URL}/error`;
          }
          return;
        case "undefined":
          window.location.href =
            window.eco.serviceType === "OCI"
              ? getWorkspaceName()
                ? `${process.env.PUBLIC_URL}/${getWorkspaceName()}/home`
                : `${process.env.PUBLIC_URL}/My Space/home`
              : loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
              ? `${process.env.PUBLIC_URL}/myInbox/received`
              : `${process.env.PUBLIC_URL}/home`;
          return;
        default:
          window.location.href = `${process.env.PUBLIC_URL}/error`;
      }
    }
  }, [window.location.pathname, workgroupState.workgroupSidemenuList]);
  useEffect(() => {
    window.addEventListener("dragend", handleDragEnd);
    window.addEventListener("message", receiveMessage);
    return () => {
      window.removeEventListener("dragend", handleDragEnd);
      window.removeEventListener("message", receiveMessage);
    };
  }, []);
  useEffect(() => {
    changeDragFiles(documentState.dragItem);
    if (documentState.dragDirectly) {
      toggleOpenFloatAiPage(true);
    }
  }, [documentState.dragItem]);
  const setUserInfoToState = () => {
    if (getUserInfoFromLocalStorage() === null) {
      if (
        getAdminInfoFromLocalStorage() !== null &&
        window.location.href.includes("/admin")
      ) {
        window.location.href = `${process.env.PUBLIC_URL}/admin`;
      } else if (!window.location.href.endsWith("/login")) {
        setRedirectUrl(window.location.href);
        window.location.href = `${process.env.PUBLIC_URL}/login`;
      } else {
        window.location.href = `${process.env.PUBLIC_URL}/login`;
      }
    } else if (loginDto?.ecoUserInfo?.userType === 3) {
      removeAccessAdminToken();
      removeAdminInfoFromLocalStorage();
      removeAccessToken();
      removeUserInfoFromLocalStorage();
      if (window.location.href.includes("/admin")) {
        window.location.href = `${process.env.PUBLIC_URL}/admin`;
      } else {
        window.location.href = `${process.env.PUBLIC_URL}/login`;
      }
    } else {
      setPageReady(true);
      const user = getUserInfoFromLocalStorage() as string | "";
      const loginDto: LoginDto = JSON.parse(user);
      if (!loginDto) return;
      getMembersProfile(loginDto?.userInfo?.userId);

      // LITE일 경우, 앱연동 필요없음
      if (
        window.eco.companyName !== "linkApp" ||
        loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
      )
        return;
      if (!loginState.appRegisterStatus?.dropbox) {
        getAppLoginInfo("dropbox");
      }
      if (
        utilsCommon.checkMenuAvailable("zoom") &&
        !loginState.appRegisterStatus?.zoom
      ) {
        getAppLoginInfo("zoom");
      }
    }
  };
  // 브라우저 종료를 통한 웹 리비전 완료 확인 리스너
  const receiveMessage = useCallback(
    async (e: any) => {
      if (!e.data.xFasooSignature || e.data.xFasooSignature !== "Fasoo") return;
      if (e.data.xFasooAction && e.data.xFasooAction.trim !== "") {
        const actionParam = e.data.xFasooActionParam;
        if (e.data.xFasooAction === "endWebEdit") {
          const newSyncInfo = await docApi.getDocSyncInfo({
            workgroupId: actionParam.workgroupId,
            syncId: actionParam.syncId,
          });

          if (
            newSyncInfo &&
            newSyncInfo.revisionNo !== parseInt(actionParam.revisionNo)
          ) {
            toasts.toastMsg(nanoid(), t("file.alert.endInforce"), "error");
          } else {
            // 웹 체크인 api
            toasts.toastMsg(
              nanoid(),
              t("file.alert.completedWebRevise"),
              "success"
            );
            completeCheckinByWeb(
              actionParam.workgroupId,
              actionParam.syncId,
              actionParam.revisionNo
            );
          }

          if (actionParam.workgroupId !== "DUMMYID") {
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }

          // 페이지 새로고침 추가 필요
        } else if (
          e.data.xFasooAction === "startWebEdit" ||
          e.data.xFasooAction === "fileDelete"
        ) {
          if (actionParam.workgroupId !== "DUMMYID") {
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        }
        // else if (e.data.xFasooAction === "uploadFile") {
        //   changeDragAttachments(actionParam.filter((item: any) => item));
        //   toggleSelectWorkgroupModal(true);
        // }
      }
    },
    [completeCheckinByWeb, toasts]
  );
  /** 문서 테이블 표시 페이지 확인 */
  const isDocPage = (url?: string): boolean => {
    return (
      !!url &&
      ((url?.includes("/workgroup/") && url.includes("/file")) ||
        url?.includes("/myInbox") ||
        url?.includes("/favorite"))
    );
  };
  const onDragStart = (event: any): void => {
    const emptyImg = new Image();
    const dropboxItem = event.currentTarget.closest(
      ".dropbox-item"
    ) as HTMLElement | null;

    emptyImg.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";
    event.dataTransfer.setDragImage(emptyImg, 0, 0);
    changeDragFiles(
      (dropboxItem?.id ?? event.target.id)
        .substring("appState.dropboxFile:".length)
        .split(", ")
        .filter((item1: string) => item1 !== "")
        .map((item: string) => {
          const file = JSON.parse(item);
          return {
            contentName: file.name,
            size: file.size,
            id: file.id,
          };
        })
    );
  };
  /** UI: 기본 화면 상단 */
  const renderHeader = () => {
    return (
      <Box classes="ecoheader">
        <Box
          classes="cursor pointer"
          onClick={() =>
            navigate(
              utilsCommon.renderProperNavigatePath(
                loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                  ? `/myInbox/received`
                  : `/home`
              )
            )
          }
        >
          <img
            src={utilsCommon.renderWrapsodyECOLogo()}
            alt={"logo"}
            height={32}
            width={129}
          />
        </Box>
        {!window.location.href.includes("/linkPortal") && <RootSearchbar />}
        <Box classes="mg ml-auto" alignItems="center">
          {(window.eco.useCsChat === "https://chat.fasoo.com" ||
            window.eco.useCsChat === "https://chatdev.fasoo.com") && (
            <Button
              classes="mg ml-20"
              btnStyle="icon"
              size={"sm"}
              onClick={async () => {
                try {
                  const resp = await axios.get<CsChat>(
                    `${GATEWAY_URL}/wapi/chat/csChat`
                  );
                  if (resp.data.payload) {
                    window.open(
                      `${
                        process.env.PUBLIC_URL
                      }/html/cs-request.html?payload=${encodeURIComponent(
                        resp.data.payload
                      )}&chatUrl=${encodeURIComponent(window.eco.useCsChat)}`,
                      "_blank",
                      "width=500,height=700,toolbar=no,menubar=no,scrollbars=no,resizable=no"
                    );
                  } else {
                    toasts.toastMsg(
                      nanoid(),
                      t("common.alert.serverError"),
                      "error"
                    );
                  }
                } catch (err) {
                  return Promise.resolve({ error: err });
                }
              }}
              leadingIcon={<Icons variant="fill" label={"bot"} />}
            />
          )}
          {!utilsCommon.isDemoEco() && (
            <Dropdown
              buttonWidth={240}
              style={{ width: "32px", justifyContent: "center" }}
              classes="mg ml-8 ecoheader-service"
              labelNode={<Icons variant="fill" label="questionmark" />}
              align={"right"}
            >
              <DropdownItem
                label={t("login.login.serviceAndDownload")}
                onClick={() => window.open(`${process.env.PUBLIC_URL}/service`)}
              />
            </Dropdown>
          )}
          {!window.location.href.includes("/linkPortal") && (
            <Dropdown
              width={420}
              buttonWidth={420}
              classes="mg ml-8 ecoheader-notification"
              labelNode={
                <Icons
                  variant="fill"
                  label="notification"
                  isAlert={loginState.alarmResponse?.unreadCount > 0}
                />
              }
              align={"right"}
            >
              <Notification />
            </Dropdown>
          )}
          <Box classes="ecoheader-profile mg ml-8">
            <HeaderUserDropdown />
          </Box>
        </Box>
      </Box>
    );
  };
  const [isMyBoxOpen, toggleIsMyBoxOpen] = useState(true);
  /** UI: 기본 좌측 사이드 메뉴 */
  const renderSidemenu = () => {
    const showType = utilsCommon.checkSideMenuShowNestedType(loginDto)
      ? "nested"
      : "";
    const tabs = [
      {
        id: "home",
        path: "/home",
        invisible:
          loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE",
        name: t("common.home"),
        icon: <Icons variant="fill" label="home" />,
      },

      {
        id: "NONE",
        path: "/myInbox/received",
        name: "My Box",
        icon: <Icons variant="fill" label="user" />,
        trailingFunction: () => toggleIsMyBoxOpen(!isMyBoxOpen),
        trailingIcon: (
          <Icons
            variant={"fill"}
            label={"chevron"}
            direction={isMyBoxOpen ? "down" : "up"}
          />
        ),
      },
      {
        id: "/myInbox/received",
        path: "/myInbox/received",
        name: t("sendingFile.received"),
        invisible: !isMyBoxOpen,
        icon: (
          <Box width={"20px"} classes="mg ml-4" justifyContent="center">
            <Icons variant="fill" label="dot" />
          </Box>
        ),
      },
      {
        id: "/myInbox/sent",
        path: "/myInbox/sent",
        name: t("sendingFile.send"),
        icon: (
          <Box width={"20px"} classes="mg ml-4" justifyContent="center">
            <Icons variant="fill" label="dot" />
          </Box>
        ),
        invisible: !isMyBoxOpen || loginDto?.ecoUserInfo?.userType !== 1,
        trailingFunction:
          utilsCommon.checkVisibleOptions("sendFile") &&
          ((e: any) => {
            e.stopPropagation();
            changeOpenExtraModal("SEND_FILE");
          }),
      },
      {
        id: "fav",
        path: "/favorite",
        name: t("bookmark.title"),
        icon: <Icons variant="fill" label="star" />,
      },
      {
        id: "log",
        path: "/log",
        name: t("log.myPolicyTitle"),
        icon: <Icons variant="fill" label="layers" />,
      },
      {
        id: "/trash",
        path: "/trash",
        name: t("trash.title"),
        icon: <Icons variant="fill" label="delete" />,
      },
      {
        id: "/workgroups",
        path: "/workgroups",
        name: t("common.workgroup"),
        invisible:
          loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE",
        icon: <Icons variant="fill" label="users" />,
        trailingFunction:
          utilsCommon.checkWorkgroupCreatePermission(loginDto) &&
          ((e: any) => {
            e.stopPropagation();
            changeOpenExtraModal("CREATE_NEW_WORKGROUP");
          }),
      },
    ];
    const adminTabs = [
      {
        id: "manageWorkgroup",
        path: "/manageWorkgroup",
        name: t("admin.menu.workgroup"),
        icon: <Icons variant="fill" label="users" />,
      },
      {
        id: "manageDept",
        path: "/manageDept",
        name: t("admin.menu.dept"),
        icon: <Icons variant="fill" label="company" />,
      },
      {
        id: "manageConfig",
        path: "/manageConfig",
        name: t("admin.menu.config"),
        icon: <Icons variant="fill" label="setting" />,
      },
      {
        id: "workgroupLog",
        path: "/workgroupLog",
        name: t("admin.menu.workgroupLog"),
        icon: <Icons variant="fill" label="log" />,
      },
      {
        id: "fileLog",
        path: "/fileLog",
        name: t("admin.menu.log"),
        icon: <Icons variant="fill" label="layers" />,
      },
    ];
    const handleSetDefaultspace = async (targetWorkspace: string) => {
      setButtonClick(buttonClick + 1);
      const workspaceInfo = workspaceStore?.workspaceSideList?.filter(
        (item: any) => item.dsdCode === targetWorkspace
      )[0];
      const ecoUser = window.localStorage.getItem("fasoo-eco-user");
      let temp = {} as any;
      temp = JSON.parse(ecoUser ? ecoUser : "");
      const resp = await updateWorkspaceMember({
        dsdCode: workspaceInfo.dsdCode,
        userId: temp.userInfo.userId,
        defaultTenancyCode: targetWorkspace,
      });
      if (resp.payload) {
        toasts.toastMsg(nanoid(), t("price.defaultWorkspaceMsg"), "success");
      } else {
        toasts.toastMsg(nanoid(), t("price.failSetting"), "error");
      }
    };
    const renderOCIStorageInfo = () => {
      return (
        <Box direction="column" classes="Wsidemenu-storage">
          <Box alignItems="center">
            <CloudIcon />
            <Typography classes="mg ml-4" type="body4_rg">
              {i18n.language.includes("ko") ? t("storage.saveStorage") : ""}
              {workspaceUtil.getStoragePercentage(
                tenancyInfo?.dsdCode === "MTECOCOMMON00001"
                  ? 1
                  : (tenancyInfo?.paymentInfo?.maxInternalUserCount ?? 0) *
                      (tenancyInfo?.paymentInfo?.storagePerUser ?? 0) +
                      (tenancyInfo?.paymentInfo?.additionalStorage ?? 0),
                tenancyInfo?.tenancyUsageSize
              )}
              {t("storage.useSaveStorageInUse")}
            </Typography>
          </Box>
          <progress
            id="progress"
            value={workspaceUtil.getStoragePercentage(
              tenancyInfo?.dsdCode === "MTECOCOMMON00001"
                ? 1
                : (tenancyInfo?.paymentInfo?.maxInternalUserCount ?? 0) *
                    (tenancyInfo?.paymentInfo?.storagePerUser ?? 0) +
                    (tenancyInfo?.paymentInfo?.additionalStorage ?? 0),
              tenancyInfo?.tenancyUsageSize
            )}
            max={100}
            className="mg mt-8"
          />
          {workspaceUtil.getLanguage() === "ko" ? (
            <Box alignItems="center">
              <Typography
                fontWeight={700}
                fontSize={12}
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {tenancyInfo?.dsdCode === "MTECOCOMMON00001"
                  ? 1
                  : (tenancyInfo?.paymentInfo?.maxInternalUserCount ?? 0) *
                      (tenancyInfo?.paymentInfo?.storagePerUser ?? 0) +
                    (tenancyInfo?.paymentInfo?.additionalStorage ?? 0)}{" "}
                GB
              </Typography>
              <Typography
                fontWeight={400}
                fontSize={12}
                classes="mg ml-4"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("storage.use")}
              </Typography>
              <Button
                textColor="#25c16f"
                size={"sm"}
                btnStyle="text"
                classes="mg ml-4 tenancySize"
                onClick={() =>
                  navigate(
                    utilsCommon.renderProperNavigatePath("/manageStorage")
                  )
                }
                label={utilsDocs.fileSizeFormat(tenancyInfo?.tenancyUsageSize)}
              />
              <Typography
                fontWeight={400}
                fontSize={12}
                classes="mg ml-4"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("storage.useOf")}
              </Typography>
            </Box>
          ) : (
            <Box alignItems="center">
              <Button
                btnStyle="text"
                textColor="#25c16f"
                size={"sm"}
                classes="tenancySize"
                onClick={() =>
                  navigate(
                    utilsCommon.renderProperNavigatePath("/manageStorage")
                  )
                }
                label={utilsDocs.fileSizeFormat(tenancyInfo?.tenancyUsageSize)}
              ></Button>
              <Typography
                fontWeight={400}
                fontSize={12}
                classes="mg ml-4"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {t("storage.use")}
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={12}
                exactColor={getColor("COLOR_AVATAR_COOL_GRAY_90")}
                classes="mg ml-4"
              >
                {tenancyInfo?.dsdCode === "MTECOCOMMON00001"
                  ? 1
                  : (tenancyInfo?.paymentInfo?.maxInternalUserCount ?? 0) *
                      (tenancyInfo?.paymentInfo?.storagePerUser ?? 0) +
                    (tenancyInfo?.paymentInfo?.additionalStorage ?? 0)}
                {workspaceUtil.getLanguage() !== "ko" && " "}
                GB
              </Typography>
            </Box>
          )}
        </Box>
      );
    };
    return (
      <SideMenu
        isOpen
        sideMenuRef={panelRef}
        id={"sidemenuId"}
        onMouseDown={(e) => handleMouseDown(e)}
        classes="sidemenu"
        style={{
          paddingTop: window.eco.serviceType === "OCI" ? "12px" : "28px",
        }}
      >
        {tenancyInfo && (
          <Box classes="sidemenu-workspace">
            <Typography
              fontSize={14}
              fontWeight={700}
              ellipsis
              lineHeight={"20px"}
              classes="mg ml-8"
            >
              {tenancyInfo?.tenancyName}
            </Typography>
            <Dropdown
              buttonClick={buttonClick}
              classes="mg ml-auto"
              positionStrategy="fixed"
            >
              <Box width={300} classes="sidemenu-workspace--item">
                <Avatar
                  name={tenancyInfo?.tenancyName}
                  uniqueCode={tenancyInfo?.alias ?? ""}
                  color={
                    tenancyInfo?.alias ? pickColor(tenancyInfo?.alias) : "green"
                  }
                  shape={"workgroup"}
                  size={"48"}
                  style={{ borderRadius: "50%", position: "relative" }}
                />
                <Box
                  direction="column"
                  justifyContent="center"
                  classes="mg ml-8"
                  maxWidth={"calc(100% - 56px)"}
                >
                  <Typography type="subtitle1" ellipsis>
                    {tenancyInfo?.tenancyName}
                  </Typography>
                  <Typography
                    type="body4_rg"
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    cloud.wrapsodyeco.com/{tenancyInfo?.alias}
                  </Typography>
                </Box>
              </Box>
              <DropdownItem
                label={t("price.defaultWorkspace")}
                disabled={
                  tenancyInfo?.myDefaultTenancy ||
                  tenancyInfo?.tenancyStatus === "PAUSE"
                }
                onClick={() => handleSetDefaultspace(tenancyInfo?.dsdCode)}
              />
              {tenancyInfo.tenancyStatus === "PAUSE" && (
                <DropdownItem
                  onClick={() => {
                    navigate(
                      utilsCommon.renderProperNavigatePath(
                        `/linkPortal?type=manage&target=${tenancyInfo?.alias}`
                      )
                    );
                  }}
                  label={t("bill.PurchaseManagement")}
                />
              )}
              {tenancyInfo?.dsdCode !== "MTECOCOMMON00001" && (
                <DropdownItem
                  label={t("price.toLeaceWorkspcae")}
                  onClick={() =>
                    changeConfirmUnregisterModalId(tenancyInfo.alias)
                  }
                  disabled={
                    tenancyInfo?.masterUserId ===
                      (loginDto?.userInfo?.userId ?? "") ||
                    tenancyInfo.tenancyStatus === "PAUSE"
                  }
                />
              )}
            </Dropdown>
          </Box>
        )}
        <Box classes="sidemenu-top overflow-scroll">
          {tabs
            .filter((item1) => !item1.invisible)
            .map((item) => (
              <Box
                classes={classNames("sidemenu-button", {
                  hasIcon: !!item.trailingFunction,
                  selected:
                    (tab.includes(item.id) &&
                      !window.location.pathname.includes("/workgroup/")) ||
                    (tab === "/" &&
                      (loginDto?.ecoSystemLicenseStats?.featureAccessType ===
                      "LITE"
                        ? item.id === "/myInbox/received"
                        : item.id === "home")),
                })}
                onClick={() => {
                  navigate(utilsCommon.renderProperNavigatePath(item.path));
                }}
              >
                {item.icon}
                <Typography
                  classes="mg ml-12"
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {item.name}
                </Typography>
                {item.trailingFunction && (
                  <Button
                    btnStyle="icon"
                    size="sm"
                    classes="mg ml-auto"
                    onClick={item.trailingFunction}
                    leadingIcon={
                      item.trailingIcon ?? (
                        <Icons variant="fill" label="plusCircle" />
                      )
                    }
                  />
                )}
              </Box>
            ))}
          {loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" && (
            <SideMenuAccordion
              dropDownDirection
              type="sidemenuNew"
              text={t("common.starrredWorkgroup")}
            >
              {workgroupState.workgroupSidemenuList
                ?.slice()
                ?.filter(
                  (item1: Workgroup) =>
                    item1.favorite === "Y" && item1.workgroupJoined
                )
                ?.sort((a: Workgroup, b: Workgroup) =>
                  a.workgroupName.localeCompare(b.workgroupName, "en", {
                    numeric: true,
                  })
                )
                .map((item: Workgroup) => (
                  <Box
                    classes={classNames("sidemenu-button", {
                      selected: tab.includes(item.workgroupId),
                    })}
                    onClick={() => {
                      if (
                        "workgroupJoined" in item &&
                        item?.workgroupJoined === false
                      )
                        return;
                      navigate(
                        utilsCommon.renderProperNavigatePath(
                          item.workgroupType === "HUB"
                            ? `/workgroup/${item.workgroupId}/log`
                            : utilsCommon.checkMenuAvailable("zoom") ===
                                false &&
                              utilsCommon.getChatAvailibility() === "noDisplay"
                            ? `/workgroup/${item.workgroupId}/file`
                            : `/workgroup/${item.workgroupId}/home`
                        )
                      );
                    }}
                  >
                    <WorkgroupAvatar type="sidemenu" item={item} size="20" />
                    <Tooltip
                      message={item.workgroupName}
                      maxWidth={"calc(100% - 20px)"}
                    >
                      <Typography
                        classes="mg ml-12"
                        type="body2_rg"
                        exactColor={getColor(
                          window.location.href.includes(item.workgroupId)
                            ? "COLOR_TEXT_INVERSE"
                            : item.isValidate === 0
                            ? "COLOR_TEXT_DISABLED"
                            : "COLOR_TEXT_SECONDARY"
                        )}
                        ellipsis
                      >
                        {item.workgroupName}
                      </Typography>
                    </Tooltip>
                  </Box>
                ))}
            </SideMenuAccordion>
          )}
          {loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" && (
            <SideMenuAccordion
              icon={
                <Tooltip
                  message={
                    !sideMenuSortby
                      ? t("common.sortDateNameAsc")
                      : t("common.sortDateNameDesc")
                  }
                >
                  <Button
                    btnStyle="icon"
                    leadingIcon={
                      <Box>
                        <Icons
                          variant="line"
                          label="exchangeUD"
                          stroke={getColor("COLOR_ICON_INFO")}
                        />
                      </Box>
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      const newDirection = !sideMenuSortby;
                      getWorkgroupSideList({
                        ...SIDE_WORKGROUPLIST_FILTER,
                        orderByType: newDirection ? "ASC" : "DESC",
                      });
                      changeSidemeSortBy(newDirection);
                    }}
                  />
                </Tooltip>
              }
              type="sidemenuNew"
              dropDownDirection
              text={t("common.all")}
            >
              {showType === "nested" ? (
                <NestedSidemenu tab={tab} sideMenuSortby={sideMenuSortby} />
              ) : (
                workgroupState.workgroupSidemenuList
                  ?.filter((item1: Workgroup) => item1.workgroupJoined)
                  ?.map((item: Workgroup) => (
                    <Box
                      classes={classNames("sidemenu-button", {
                        selected: tab.includes(item.workgroupId),
                      })}
                      onClick={() =>
                        navigate(
                          utilsCommon.renderProperNavigatePath(
                            item.workgroupType === "HUB"
                              ? `/workgroup/${item.workgroupId}/log`
                              : utilsCommon.checkMenuAvailable("zoom") ===
                                  false &&
                                utilsCommon.getChatAvailibility() ===
                                  "noDisplay"
                              ? `/workgroup/${item.workgroupId}/file`
                              : `/workgroup/${item.workgroupId}/home`
                          )
                        )
                      }
                    >
                      <WorkgroupAvatar type="sidemenu" item={item} size="20" />
                      <Tooltip
                        message={item.workgroupName}
                        maxWidth={"calc(100% - 20px)"}
                      >
                        <Typography
                          classes="mg ml-12"
                          type="body2_rg"
                          exactColor={getColor(
                            window.location.href.includes(item.workgroupId)
                              ? "COLOR_TEXT_INVERSE"
                              : item.isValidate === 0
                              ? "COLOR_TEXT_DISABLED"
                              : "COLOR_TEXT_SECONDARY"
                          )}
                          ellipsis
                        >
                          {item.workgroupName}
                        </Typography>
                      </Tooltip>
                    </Box>
                  ))
              )}
            </SideMenuAccordion>
          )}
          {tenancyInfo?.masterUserId === myId &&
            window.eco.serviceType === "OCI" && (
              <SideMenuAccordion
                dropDownDirection
                type="sidemenuNew"
                text={t("common.setting")}
              >
                {adminTabs.map((item) => (
                  <Box
                    classes={classNames("sidemenu-button", {
                      selected:
                        (tab.includes(item.id) &&
                          !window.location.pathname.includes("/workgroup/")) ||
                        (tab === "/" && item.id === "home"),
                    })}
                    onClick={() => {
                      navigate(utilsCommon.renderProperNavigatePath(item.path));
                    }}
                  >
                    {item.icon}
                    <Typography
                      classes="mg ml-12"
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                ))}
              </SideMenuAccordion>
            )}
        </Box>
        {tenancyInfo &&
          (tenancyInfo?.masterUserId === myId ||
            tenancyInfo?.paymentCode === "DEFAULT" ||
            tenancyInfo?.dsdCode === "MTECOCOMMON00001") &&
          renderOCIStorageInfo()}
        <Box
          classes="mg mt-auto logo"
          alignItems="center"
          justifyContent={
            window.eco.serviceType === "OCI" ? "flex-start" : "center"
          }
          height={window.eco.serviceType === "OCI" ? 56 : 14}
        >
          {window.eco.serviceType === "OCI" && (
            <img
              src={require("./shared/images/logo/logo_wrapsody_eco_248.png")}
              alt={"logo"}
              width={96}
              height={24}
              className="mg mr-8"
            />
          )}
          <Typography
            fontSize={14}
            lineHeight={"14px"}
            fontWeight={400}
            exactColor={"#717985"}
          >
            © Fasoo
          </Typography>
        </Box>
      </SideMenu>
    );
  };
  const renderSideTenancyList = () => {
    return (
      <Box direction="column" width={48} classes="Wsidemenu-workspaces">
        {workspaceStore?.workspaceSideList?.map((item: WorkspaceDTO) => (
          <WorkspaceAvatar item={item} />
        ))}
        <Avatar
          uniqueCode={""}
          icon="plus"
          classes="cursor pointer"
          onClick={() =>
            navigate(
              utilsCommon.renderProperNavigatePath("/linkPortal?type=pay")
            )
          }
        />
      </Box>
    );
  };
  /** 문서 드래그 끝날 경우, 사용 */
  const handleDragEnd = (e: DragEvent): void => {
    e.preventDefault();
    changeDragFiles([]);
  };
  /** UI" 드래그 되고있는 문서 표시 */
  const renderDragItem = (
    subject: string,
    dragItemLength: number
  ): React.ReactNode => {
    return (
      <Box classes="aiChat-dragItems" id={"aiChat-dragItems"}>
        <Icons
          variant="line"
          label="ban"
          id="aiChat-notAllowed"
          classes="aiChat-notAllowed"
          stroke={"#EF5350"}
        />
        <Box classes="aiChat-mainItem">
          <Typography ellipsis lineHeight="normal" type="body2_rg">
            {subject}
          </Typography>
        </Box>
        {dragItemLength > 1 && (
          <>
            <Box classes="aiChat-otherItems " />
            <Box classes="aiChat-number">{dragItemLength}</Box>
          </>
        )}
      </Box>
    );
  };
  const onDragOverFile = (
    event: React.DragEvent<HTMLDivElement>,
    isInbox?: boolean
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'

    const dragItem = document.getElementById("aiChat-dragItems");
    const mouseX = event.clientX; // 마우스의 X 좌표
    const mouseY = event.clientY; // 마우스의 Y 좌표
    if (dragItem && (dragItem.style || (mouseX === 0 && mouseY === 0))) {
      dragItem.style.display = "block";
      dragItem.style.top =
        (mouseY > window.innerHeight / 2 ? mouseY - 48 : mouseY) + "px";
      if (direction === "ltr") {
        dragItem.style.insetInlineStart =
          (mouseX > window.innerWidth / 2 ? mouseX - 240 : mouseX) + "px";
      } else {
        dragItem.style.insetInlineEnd =
          (mouseX < window.innerWidth / 2 ? mouseX + 240 : mouseX) + "px";
      }
    }
    const notAllowedIcon = document.getElementById("aiChat-notAllowed");
    if (isInbox && notAllowedIcon) {
      notAllowedIcon.style.display = "none";
    } else if (notAllowedIcon) {
      notAllowedIcon.style.display = "";
    }
  };
  const handleUploadFilesInUID = async (
    fileList: any[],
    type: "GOOGLE" | "DROPBOX",
    isInBox: boolean,
    ecoToDrivePath?: string
  ): Promise<void> => {
    const uuid = nanoid();
    !isInBox && toggleOpenFileUploadModal(true);
    fileList.forEach(async (targetFile, index) => {
      const file = targetFile;
      const uploadFile: UploadFile = {
        isMultiple: !isInBox,
        uuid: uuid + index,
        fileName: file.contentName,
        fileSize: file.size,
        workgroupId: "",
        workgroupName: "",
        status: "WAIT", // WAIT, ING, SUCCESS, FAIL
        errorMsg: "",
        fileType: type,
      };
      setAddUploadProgressFiles(uploadFile);
    });
    for (const [index, file] of fileList.entries()) {
      let targetUid = "";
      if (!ecoToDrivePath) {
        const res = await getDocUploadUidCreate();
        targetUid = "" + res.payload;
      }
      const fileExt = file.contentName.split(/[.]+/).pop();
      let msg = "";
      let checkExt = true;
      const size = file.size
        ? typeof file.size === "string"
          ? parseInt(file.size)
          : file.size
        : typeof file.fileSize === "string"
        ? parseInt(file.fileSize)
        : file.fileSize;
      setUpdateUploadProgressFiles({
        uuid: uuid + index,
        fileName: file.contentName,
        fileId: file.id,
        fileSize: size,
        workgroupId: "",
        workgroupName: "",
        status: "ING", // WAIT, ING, SUCCESS, FAIL
        fileType: type,
        errorMsg: "",
      });
      if (
        loginDto.serviceConfig.disableExtensions.findIndex(
          (ext) => ext === fileExt
        ) > -1
      ) {
        msg = t("file.alert.notSupportedExt"); // by iris
        checkExt = false;
      } else if (file.size === 0) {
        msg = t("file.alert.notAllowZeroSize"); // by iris
        checkExt = false;
      } else if (
        loginDto &&
        size > loginDto.ecoCommonConfig.fileUploadMaxSizeMB * 1024 * 1024
      ) {
        msg = t("file.alert.notAllowMaxSize", {
          num: loginDto.ecoCommonConfig.fileUploadMaxSizeMB,
        });
        checkExt = false;
      }
      let resp: any;
      resp = !ecoToDrivePath
        ? await uploadDropboxToEco({
            uid: targetUid,
            fileId: file.id,
            name: file.contentName,
          })
        : await uploadEcoToDrive({
            type: "dropbox",
            syncId: file.syncId,
            revisionNo: file.revisionNo,
            targetFolder: ecoToDrivePath === "/" ? "" : ecoToDrivePath,
          });
      if (!isInBox || resp.error || ecoToDrivePath)
        setUpdateUploadProgressFiles({
          uuid: uuid + index,
          fileName: file.contentName,
          fileId:
            resp.payload && resp.payload.entity
              ? resp.payload.entity.fileId
              : file.id ?? "",
          fileSize: size,
          workgroupId: "",
          workgroupName: "",
          status: !checkExt || resp.error ? "FAIL" : "SUCCESS", // WAIT, ING, SUCCESS, FAIL
          fileType: type,
          errorMsg: msg ? msg : resp.error ? resp.error.message : "",
        });
      else {
        const workgroupId = window.location.href.split("/")[4] ?? "";
        if (
          resp.error ||
          !workgroupId ||
          !window.location.href.includes("/file") ||
          !!ecoToDrivePath
        )
          return;
        const folderId = window.location.href.split("?folderId=")[1] ?? "";
        const completeResp: any = await doDocUploadFileComplete(targetUid, {
          workgroupId,
          permissionList: [{ changeCode: "CREATE", targetType: "WORKGROUP" }],
          folderId: folderId,
        });
        if (completeResp.payload) {
          setUpdateUploadProgressFiles({
            uuid: uuid + index,
            fileName: file.contentName,
            fileSize: size,
            workgroupId: workgroupId,
            workgroupName: "",
            status: completeResp.payload.error ? "FAIL" : "SUCCESS", // WAIT, ING, SUCCESS, FAIL
            errorMsg: resp ?? completeResp.payload.error?.message ?? "",
          });
        } else if (completeResp?.error) {
          toasts.toastMsg(
            nanoid(),
            completeResp?.error?.message ?? t("common.alert.internalError"),
            "error"
          );
        }
      }
    }
  };
  const onDropFile = (
    event: React.DragEvent<HTMLDivElement>,
    isInBox?: boolean,
    type?: string,
    targetFolder?: string
  ): void => {
    // event.preventDefault();
    if (isInBox) {
      event.stopPropagation();
      if (type === "file" && window.location.href?.includes("/file")) {
        if (!utilsCommon.checkUploadFilePermission(loginDto)) {
          toasts.toastMsg(
            nanoid(),
            t("file.alert.uploadNoPermission"),
            "error"
          );
          return;
        }
        if (!("syncId" in dragFiles[0])) {
          handleUploadFilesInUID(dragFiles as any, "DROPBOX", true);
        }
      } else if (!("syncId" in dragFiles[0])) {
        handleUploadFilesInUID(dragFiles as any, "DROPBOX", false);
      } else if (type === "upload") {
        handleUploadFilesInUID(
          dragFiles as any,
          "DROPBOX",
          true,
          targetFolder ?? "/"
        );
      }
    }
  };
  /** 스페이스 나가기 */
  const handleUnregisterworkspace = async (
    deleteWorkspaceAlias: string
  ): Promise<void> => {
    const deleteWorkspaceCode = workspaceStore?.workspaceSideList?.filter(
      (item: any) => item.alias === deleteWorkspaceAlias
    )[0].dsdCode;
    if (!myId || !deleteWorkspaceCode) return;

    const resp: any = await deleteMember([myId], deleteWorkspaceCode);
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("account.leaveSuccess"), "success");
      getWorkspaceList();
      navigate(config.url("/My Space/home"));
    } else if (resp.error.name === "NOT_FOUND_MEMBER") {
      toasts.toastMsg(nanoid(), t("bill.noExistWorkspace"), "error");
    } else if (resp.error.name === "NOT_ALLOW_MULTI_USER") {
      toasts.toastMsg(nanoid(), t("bill.noRemoveMultiple"), "error");
    } else if (resp.error.name === "IS_WORKSPACE_MASTER") {
      toasts.toastMsg(nanoid(), t("bill.noWithdrawWorkspace"), "error");
    } else if (resp.error.name === "DENY_PERMISSION") {
      toasts.toastMsg(nanoid(), t("bill.noPermission"), "error");
    } else {
      toasts.toastMsg(nanoid(), t("bill.leaveFail"), "error");
    }
  };
  return pageReady ? (
    <Box
      direction="column"
      height="100%"
      onDragOver={onDragOverFile}
      onDrop={onDropFile}
    >
      {confirmUnregisterModalId !== "" && (
        <Modal
          open={confirmUnregisterModalId !== ""}
          title={t("bill.leave")}
          onClose={() => changeConfirmUnregisterModalId("")}
          body={t("price.leaveWorkspace")}
          width={538}
          footer={
            <Box justifyContent="flex-end">
              <Button
                btnStyle="fill"
                type="secondary"
                onClick={() => changeConfirmUnregisterModalId("")}
                label={t("common.cancel")}
              />
              <Button
                onClick={async () => {
                  await handleUnregisterworkspace(confirmUnregisterModalId);
                  changeConfirmUnregisterModalId("");
                  navigate(utilsCommon.renderProperNavigatePath(`/home`));
                  window.location.reload();
                }}
                btnStyle="fill"
                type="error"
                textColor="red"
                classes="mg ml-8"
                label={t("workgroup.exit")}
              />
            </Box>
          }
        />
      )}
      {["SEND_FILE", "CREATE_NEW_WORKGROUP"].includes(openExtraModal) && (
        <WorkgroupAddModal
          createType={
            openExtraModal === "SEND_FILE"
              ? "SEND_FILE"
              : "CREATE_NEW_WORKGROUP"
          }
          open
          onClose={() => changeOpenExtraModal("CLOSE")}
        />
      )}
      {openAddLinkModal && (
        <LinkApp
          open={openAddLinkModal}
          onClose={() => toggleOpenAddLinkModal(false)}
          changeTargetOpenAppLinkPortal={changeTargetOpenAppLinkPortal}
        />
      )}
      {openFileUploadModal && (
        <WorkgroupAddModal
          createType={"SEND_FILE"}
          open={openFileUploadModal}
          targetType={"DROPBOX"}
          onClose={() => {
            setUpdateUploadProgressFiles({} as UploadFile);
            toggleOpenFileUploadModal(false);
          }}
        />
      )}
      {renderHeader()}
      {dragFiles?.length > 0 &&
        !documentState.dragDirectly &&
        renderDragItem(dragFiles[0].contentName, dragFiles.length)}
      {window.eco.serviceType === "OCI" &&
      window.location.href.includes("/linkPortal?") ? (
        <Box height={"calc(100% - 61px)"} width={"100%"}>
          <Routes>
            <Route path="/:workspace/linkPortal" element={<LinkPortalPage />} />
          </Routes>
        </Box>
      ) : (
        <Box height={"calc(100% - 61px)"}>
          {window.eco.serviceType === "OCI" && renderSideTenancyList()}
          {renderSidemenu()}
          <Box
            id="body"
            width={"100%"}
            height={"100%"}
            style={{ position: "relative", overflow: "hidden" }}
          >
            <Routes>
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/home`
                    : `${process.env.PUBLIC_URL}/home`
                }
                element={<HomePage />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space`
                    : `${process.env.PUBLIC_URL}`
                }
                element={
                  loginDto?.ecoSystemLicenseStats?.featureAccessType ===
                  "LITE" ? (
                    <MyInbox tab={0} />
                  ) : (
                    <HomePage />
                  )
                }
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/search`
                    : `${process.env.PUBLIC_URL}/search`
                }
                element={<Search />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/favorite`
                    : `${process.env.PUBLIC_URL}/favorite`
                }
                element={<Favorite />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/log`
                    : `${process.env.PUBLIC_URL}/log`
                }
                element={<DocLog isAdmin={false} />}
              />
              <Route
                id={"received"}
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/myInbox/received`
                    : `${process.env.PUBLIC_URL}/myInbox/received`
                }
                element={<MyInbox tab={0} />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/myInbox/sent`
                    : `${process.env.PUBLIC_URL}/myInbox/sent`
                }
                element={<MyInbox tab={1} />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/trash`
                    : `${process.env.PUBLIC_URL}/trash`
                }
                element={<Trash />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/workgroups`
                    : `${process.env.PUBLIC_URL}/workgroups`
                }
                element={<WorkgroupListPage />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/workgroup/:workgroupId/:tab`
                    : `${process.env.PUBLIC_URL}/workgroup/:workgroupId/:tab`
                }
                element={<WorkgroupPage />}
              />
              <Route
                path={
                  window.eco.serviceType === "OCI"
                    ? `${process.env.PUBLIC_URL}/:space/workgroup/:workgroupId/*`
                    : `${process.env.PUBLIC_URL}/workgroup/:workgroupId/*`
                }
                element={<WorkgroupPage />}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/:space/manageStorage`}
                element={<ManageStorage />}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/:space/manageWorkgroup`}
                element={
                  <Box classes="body">
                    <WorkgroupManage />
                  </Box>
                }
              />
              <Route
                path={`${process.env.PUBLIC_URL}/:space/manageDept`}
                element={
                  <Box classes="body">
                    <AdminSetting />
                  </Box>
                }
              />
              <Route
                path={`${process.env.PUBLIC_URL}/:space/manageConfig`}
                element={
                  <Box classes="body">
                    <Config />
                  </Box>
                }
              />
              <Route
                path={`${process.env.PUBLIC_URL}/:space/workgroupLog`}
                element={
                  <Box classes="body">
                    <WorkgroupLog />
                  </Box>
                }
              />
              <Route
                path={`${process.env.PUBLIC_URL}/:space/fileLog`}
                element={
                  <Box classes="body">
                    <DocLog isAdmin={true} />
                  </Box>
                }
              />
            </Routes>
            <Box classes="uploadFileList"> </Box>
          </Box>
          {!!targetOpenAppLinkPortal && (
            <LinkAppPortal
              target={targetOpenAppLinkPortal}
              open={!!targetOpenAppLinkPortal}
              changeTarget={(item) => changeTargetOpenAppLinkPortal(item)}
              onDragStart={onDragStart}
              onDragOverFile={onDragOverFile}
              onDropFile={onDropFile}
              // handleDeleteDoc={handleDeleteDoc}
              dragFiles={dragFiles}
              // openCreateWorkgroupModal={(type: string) =>
              //   changeWorkgroupAddModalType(type)
              // }
              onClose={() => changeTargetOpenAppLinkPortal(undefined)}
              openCreateWorkgroupModal={(type) => changeOpenExtraModal(type)}
            />
          )}
          {dragFiles?.filter((item) => !item.syncId)?.length > 0 && (
            <div
              className="dragAcceptPage"
              style={{
                width: `calc(100% - ${
                  310 +
                  (document
                    .getElementById("sideMenuChild2")
                    ?.getBoundingClientRect()?.width
                    ? Number(
                        document
                          .getElementById("sideMenuChild2")
                          ?.getBoundingClientRect()?.width
                      )
                    : 0)
                }px)`,
              }}
              onDragOver={(e) => onDragOverFile(e, true)}
              onDrop={(e) => onDropFile(e, true, "file")}
            >
              <Box direction="column">
                <img
                  src={
                    window.eco.isCspp
                      ? require("./shared/images/pages/File/emptyFilterDoc.png")
                      : require("./shared/images/pages/File/UploadFile.png")
                  }
                  alt={"UploadFile"}
                  height={110}
                />
                <Typography
                  type="h3"
                  textAlign="center"
                  exactColor={getColor("COLOR_BG_PRIMARY")}
                  classes="mg mt-20"
                >
                  {t("file.dropHere")}
                </Typography>
                <Typography
                  type="body2_rg"
                  textAlign="center"
                  exactColor={getColor("COLOR_BG_PRIMARY")}
                  classes="mg mt-10"
                >
                  {t("account.app.uploadToWorkgroup")}
                </Typography>
              </Box>
            </div>
          )}
          {window.eco.companyName === "linkApp" &&
            !utilsCommon.isDemoEco() &&
            loginDto?.ecoSystemLicenseStats?.featureAccessType !== "LITE" &&
            targetOpenAppLinkPortal === undefined && (
              <Box
                width={48}
                height={"100%"}
                direction={"column"}
                classes="rightAppLink"
                alignItems="center"
              >
                <WrapIcon />
                <DividerIcon />
                {["googleDrive", "dropbox"]
                  .filter((app) => {
                    return (
                      (app === "googleDrive" &&
                        appState.appRegisterStatus.googleDrive) ||
                      (app === "dropbox" && appState.appRegisterStatus.dropbox)
                    );
                  })
                  .map((item) => (
                    <Button
                      btnStyle="icon"
                      leadingIcon={
                        <img
                          src={require(`./shared/images/logo/${item}20.png`)}
                          alt={item}
                          width={"20px"}
                          height={"20px"}
                        />
                      }
                      onClick={() =>
                        changeTargetOpenAppLinkPortal(item as PRODUCT_ITEM_TYPE)
                      }
                    />
                  ))}
                <Button
                  onClick={() => toggleOpenAddLinkModal(true)}
                  btnStyle="icon"
                  leadingIcon={<Icons variant={"fill"} label={"plusCircle"} />}
                />
              </Box>
            )}
        </Box>
      )}
      {isDocPage(window.location.href) &&
        (openAiFloatChatPage ? (
          <div
            className="aiChat-float"
            onDragOver={(e) => onDragOverFile(e, true)}
          >
            <AiChatPage
              onClose={() => {
                toggleOpenFloatAiPage(false);
                changeDragItem([]);
              }}
              receivedDragFiles={dragFiles}
              onDropFile={onDropFile}
              // removeFile={removeFile}
            />
          </div>
        ) : (
          utilsCommon.getAIChatAvailibility() && (
            <div
              className="aiChat-badge"
              onClick={() => toggleOpenFloatAiPage(true)}
              onDragOver={() => {
                if (!openAiFloatChatPage && dragFiles?.length > 0) {
                  toggleOpenFloatAiPage(true);
                }
              }}
            >
              <AIChatIconMenu
                size={"lg"}
                borderRadius={"24px"}
                color={"color"}
              />
            </div>
          )
        ))}
    </Box>
  ) : (
    <Box direction="column" height="100%"></Box>
  );
}

export default BaseLayout;
